import React, { useEffect, useRef, useState } from 'react';

const usePeekingLoop = (visible) => {
  const [peeking, setPeeking] = useState(false);

  useEffect(() => {
    if (!visible) return;
    let alive = true;
    const runLoop = async () => {
      await new Promise((resolve) => setTimeout(resolve, 1200));
      while (alive) {
        setPeeking(true);
        await new Promise((resolve) => setTimeout(resolve, 3400));
        if (!alive) break;
        setPeeking(false);
        await new Promise((resolve) => setTimeout(resolve, 2600));
      }
    };
    runLoop();
    return () => {
      alive = false;
    };
  }, [visible, setPeeking]);

  return peeking;
};

const PeekingPotatoSVG = () => (
  <svg viewBox="0 0 240 260" className="w-32 h-36 md:w-44 md:h-48" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <radialGradient id="peek-flesh" cx="35%" cy="30%" r="80%">
        <stop offset="0%" stopColor="#FFF3D6" />
        <stop offset="60%" stopColor="#F1DDAF" />
        <stop offset="100%" stopColor="#D6B67C" />
      </radialGradient>
      <linearGradient id="towel" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#FFFFFF" />
        <stop offset="100%" stopColor="#E7E7E7" />
      </linearGradient>
    </defs>

    <g transform="translate(-6, 0)">
      <path
        d="M78,150 C58,102 96,54 145,52 C180,50 208,44 236,74 C266,106 274,166 250,214 C226,262 190,286 148,282 C104,278 72,254 62,214 C52,178 58,168 78,150 Z"
        fill="url(#peek-flesh)"
        stroke="#2a1608"
        strokeWidth="3"
      />
      <path d="M95,90 Q120,72 150,82" stroke="#fff" strokeWidth="9" opacity="0.4" strokeLinecap="round" fill="none" />
      <path d="M100,100 Q120,88 145,94" stroke="#fff" strokeWidth="3" opacity="0.55" strokeLinecap="round" fill="none" />
      <g>
        <path d="M56,205 L262,205 L268,255 L48,255 Z" fill="url(#towel)" stroke="#2a1608" strokeWidth="3" />
        <rect x="72"  y="208" width="6" height="46" fill="#FF6B6B" opacity="0.85" />
        <rect x="120" y="208" width="6" height="46" fill="#4ECDC4" opacity="0.85" />
        <rect x="168" y="208" width="6" height="46" fill="#FFD200" opacity="0.9" />
        <rect x="216" y="208" width="6" height="46" fill="#A78BFA" opacity="0.85" />
        <circle cx="90" cy="215" r="8" fill="#fff" stroke="#2a1608" strokeWidth="2.5" />
        <path d="M84,212 L96,218 M84,218 L96,212" stroke="#2a1608" strokeWidth="1.2" strokeLinecap="round" />
        <path d="M50,255 L52,262 M62,255 L64,262 M74,255 L76,262 M86,255 L88,262 M98,255 L100,262 M110,255 L112,262 M122,255 L124,262 M134,255 L136,262 M146,255 L148,262 M158,255 L160,262 M170,255 L172,262 M182,255 L184,262 M194,255 L196,262 M206,255 L208,262 M218,255 L220,262 M230,255 L232,262 M242,255 L244,262" stroke="#2a1608" strokeWidth="1.6" strokeLinecap="round" />
      </g>
      <path d="M40,120 Q30,150 42,180" stroke="#2a1608" strokeWidth="5" fill="none" strokeLinecap="round" />
      <ellipse cx="42" cy="180" rx="14" ry="10" fill="url(#peek-flesh)" stroke="#2a1608" strokeWidth="2.5" />
      <path d="M32,172 L36,160 M40,170 L42,158 M48,170 L50,158" stroke="#2a1608" strokeWidth="1.6" strokeLinecap="round" />
      <ellipse cx="120" cy="150" rx="6" ry="7" fill="#fff" stroke="#2a1608" strokeWidth="1.5" />
      <ellipse cx="170" cy="148" rx="6" ry="7" fill="#fff" stroke="#2a1608" strokeWidth="1.5" />
      <circle cx="116" cy="152" r="2.6" fill="#1a0f06" />
      <circle cx="166" cy="150" r="2.6" fill="#1a0f06" />
      <path d="M108,132 Q120,127 132,132" stroke="#2a1608" strokeWidth="2" fill="none" strokeLinecap="round" />
      <path d="M158,130 Q170,125 182,130" stroke="#2a1608" strokeWidth="2" fill="none" strokeLinecap="round" />
      <path d="M130,182 Q148,190 165,180" stroke="#2a1608" strokeWidth="2.2" fill="none" strokeLinecap="round" />
      <ellipse cx="92"  cy="178" rx="12" ry="7" fill="#FF6B6B" opacity="0.55" />
      <ellipse cx="196" cy="176" rx="12" ry="7" fill="#FF6B6B" opacity="0.55" />
      <path d="M120,26 L124,38 L136,42 L124,46 L120,58 L116,46 L104,42 L116,38 Z" fill="#FFD200" stroke="#2a1608" strokeWidth="1.2" />
    </g>
  </svg>
);

const PeekingPotato = () => {
  const wrapRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const peeking = usePeekingLoop(visible);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0]) {
        setVisible(entries[0].isIntersecting);
      }
    }, { threshold: 0.2 });
    io.observe(el);
    return () => io.disconnect();
  }, [setVisible]);

  return (
    <div
      ref={wrapRef}
      className="pointer-events-none absolute right-0 bottom-6 md:bottom-10 z-20 select-none"
      aria-hidden="true"
    >
      <div className={`peek-wrap ${peeking ? 'peek-in' : 'peek-out'}`}>
        <PeekingPotatoSVG />
        <div className={`absolute -top-2 -left-24 md:-left-32 bg-white text-black text-xs md:text-sm border-2 border-black rounded-2xl px-3 py-1.5 font-mono shadow-[3px_3px_0_#111] transition-opacity duration-500 ${peeking ? 'opacity-100' : 'opacity-0'}`}>
          still there?
        </div>
      </div>

      <style>{`
        .peek-wrap {
          transform: translateX(105%);
          transition: transform 0.9s cubic-bezier(.2,.9,.3,1);
        }
        .peek-wrap.peek-in {
          transform: translateX(30%);
          animation: bob 2.4s ease-in-out infinite 0.9s;
        }
        @keyframes bob {
          0%, 100% { transform: translateX(30%) translateY(0) rotate(0deg); }
          50%      { transform: translateX(30%) translateY(-4px) rotate(-1.5deg); }
        }
      `}</style>
    </div>
  );
};

export default PeekingPotato;
